import * as Sentry from '@sentry/react';

export const initSentry = () => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.init({
      dsn: "https://eae0c834a59380329ef21be29f64a60e@o4508572123332608.ingest.us.sentry.io/4508572136898560",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      
      // Tracing configuration
      tracesSampleRate: 1.0, // Capture 100% of transactions
      tracePropagationTargets: [
        "localhost", 
        /^https:\/\/moviehub\.com\/api/,
        /^https:\/\/api\.moviehub\.com/
      ],
      
      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Set environment
      environment: process.env.NODE_ENV,
      
      // Only send errors in production
      enabled: process.env.NODE_ENV === 'production',
      
      // Ignore certain errors
      ignoreErrors: [
        'Network request failed',
        'Load failed',
        'ResizeObserver loop limit exceeded'
      ],
      
      beforeSend(event, hint) {
        // Don't send errors in development
        if (process.env.NODE_ENV !== 'production') {
          return null;
        }

        // Remove sensitive data
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
        }

        // Clean user data
        if (event.user) {
          delete event.user.email;
          delete event.user.ip_address;
        }

        // Add MovieHub context
        event.tags = {
          ...event.tags,
          'app': 'moviehub',
          'platform': 'web'
        };

        return event;
      },

      // Configure breadcrumbs
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.category === 'console' && process.env.NODE_ENV !== 'production') {
          return null;
        }

        // Add timestamp to breadcrumb
        breadcrumb.timestamp = new Date().getTime();
        return breadcrumb;
      },
    });
  }
};

// Helper to get user-friendly error messages
export const getUserMessage = (error) => {
  if (process.env.NODE_ENV === 'development') {
    return error.message || 'An unexpected error occurred';
  }
  
  // In production, return generic messages
  if (error.response) {
    switch (error.response.status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Please log in to continue.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
  
  return 'Network error. Please check your connection.';
};

// Helper to get detailed error information in development
export const getLocalErrors = (error) => {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return {
    message: error.message,
    stack: error.stack,
    response: error.response?.data,
    status: error.response?.status,
    request: {
      method: error.config?.method,
      url: error.config?.url,
      data: error.config?.data,
    }
  };
};

// Helper to capture errors with context
export const captureError = (error, context = {}) => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.withScope((scope) => {
      // Add additional context
      scope.setTags({
        feature: context.feature || 'unknown',
        component: context.component || 'unknown',
      });

      // Add user context if available
      if (context.user) {
        scope.setUser({
          id: context.user.id,
          username: context.user.username,
          role: context.user.role,
        });
      }

      // Add extra context
      scope.setExtras(context);

      // Set error level
      scope.setLevel(context.level || 'error');

      // Capture the error
      Sentry.captureException(error);
    });
  } else {
    // In development, log to console
    console.error('Error:', error);
    console.error('Context:', context);
  }
};

// Helper to capture messages
export const captureMessage = (message, level = 'info', context = {}) => {
  if (process.env.NODE_ENV === 'production') {
    Sentry.withScope((scope) => {
      scope.setLevel(level);
      scope.setExtras(context);
      Sentry.captureMessage(message);
    });
  } else {
    console.log(`[${level}] ${message}`, context);
  }
};
